import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiFetch } from "../../shared/api";

function normalizePagedResponse(data) {
  if (Array.isArray(data)) {
    return { items: data, count: data.length };
  }
  const items = Array.isArray(data?.results) ? data.results : [];
  const count = typeof data?.count === "number" ? data.count : items.length;
  return { items, count };
}

export const fetchStockOverview = createAsyncThunk(
  "stockOverview/fetchStockOverview",
  async ({ search = "", page = 1, pageSize = 50, ordering = "name", low = false } = {}) => {
    const qp = new URLSearchParams();
    if (String(search || "").trim()) qp.set("search", String(search || "").trim());
    qp.set("ordering", ordering || "name");
    qp.set("page", String(page || 1));
    qp.set("page_size", String(pageSize || 50));
    if (low) qp.set("low", "1");
    const data = await apiFetch(`/api/product/stock-overview/?${qp.toString()}`, { method: "GET" });
    return normalizePagedResponse(data);
  }
);

// Per-store breakdown for a single product (reuses the inventory endpoint).
export const fetchProductBreakdown = createAsyncThunk(
  "stockOverview/fetchProductBreakdown",
  async ({ productId }) => {
    const data = await apiFetch(
      `/api/product/inventory/?product=${encodeURIComponent(productId)}&page_size=200`,
      { method: "GET" }
    );
    const { items } = normalizePagedResponse(data);
    return { productId, items };
  }
);

// Get-or-create a draft purchase request for a store, then add a product to it.
export const addProductToPurchaseList = createAsyncThunk(
  "stockOverview/addProductToPurchaseList",
  async ({ storeId, productId, quantity }, { rejectWithValue }) => {
    try {
      const draft = await apiFetch("/api/product/purchase-requests/draft-for-store/", {
        method: "POST",
        body: { store: storeId },
      });
      await apiFetch(`/api/product/purchase-requests/${draft.id}/items/`, {
        method: "POST",
        body: { product: productId, quantity },
      });
      return { draftId: draft.id };
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Не удалось добавить в список" });
    }
  }
);

// Auto-generate a preliminary draft list for a store from products below threshold.
export const generatePreliminaryList = createAsyncThunk(
  "stockOverview/generatePreliminaryList",
  async ({ storeId }, { rejectWithValue }) => {
    try {
      const data = await apiFetch("/api/product/purchase-requests/generate-preliminary/", {
        method: "POST",
        body: { store: storeId },
      });
      return { draftId: data.id, itemsAdded: data.items_added ?? 0 };
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Не удалось сформировать список" });
    }
  }
);

const slice = createSlice({
  name: "stockOverview",
  initialState: {
    list: {
      items: [],
      count: 0,
      status: "idle",
      error: null,
    },
    breakdownByProductId: {},
    breakdownStatusByProductId: {},
    actionStatus: "idle",
    actionError: null,
  },
  reducers: {
    clearActionState(state) {
      state.actionStatus = "idle";
      state.actionError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStockOverview.pending, (state) => {
        state.list.status = "loading";
        state.list.error = null;
      })
      .addCase(fetchStockOverview.fulfilled, (state, action) => {
        state.list.status = "succeeded";
        state.list.items = action.payload?.items || [];
        state.list.count = action.payload?.count ?? state.list.items.length;
      })
      .addCase(fetchStockOverview.rejected, (state, action) => {
        state.list.status = "failed";
        state.list.error = action.error?.message || "Не удалось загрузить остатки";
      })
      .addCase(fetchProductBreakdown.pending, (state, action) => {
        const id = String(action.meta.arg.productId);
        state.breakdownStatusByProductId[id] = "loading";
      })
      .addCase(fetchProductBreakdown.fulfilled, (state, action) => {
        const id = String(action.payload.productId);
        state.breakdownStatusByProductId[id] = "succeeded";
        state.breakdownByProductId[id] = action.payload.items || [];
      })
      .addCase(fetchProductBreakdown.rejected, (state, action) => {
        const id = String(action.meta.arg.productId);
        state.breakdownStatusByProductId[id] = "failed";
      })
      .addCase(addProductToPurchaseList.pending, (state) => {
        state.actionStatus = "saving";
        state.actionError = null;
      })
      .addCase(addProductToPurchaseList.fulfilled, (state) => {
        state.actionStatus = "succeeded";
      })
      .addCase(addProductToPurchaseList.rejected, (state, action) => {
        state.actionStatus = "failed";
        const payload = action.payload;
        state.actionError =
          (payload && typeof payload === "object" && (payload.detail || payload.message)) ||
          action.error?.message ||
          "Не удалось добавить в список";
      })
      .addCase(generatePreliminaryList.pending, (state) => {
        state.actionStatus = "saving";
        state.actionError = null;
      })
      .addCase(generatePreliminaryList.fulfilled, (state) => {
        state.actionStatus = "succeeded";
      })
      .addCase(generatePreliminaryList.rejected, (state, action) => {
        state.actionStatus = "failed";
        const payload = action.payload;
        state.actionError =
          (payload && typeof payload === "object" && (payload.detail || payload.message)) ||
          action.error?.message ||
          "Не удалось сформировать список";
      });
  },
});

export const { clearActionState } = slice.actions;
export default slice.reducer;

export const selectStockOverview = (state) => state.stockOverview.list.items || [];
export const selectStockOverviewCount = (state) => Number(state.stockOverview.list.count || 0);
export const selectStockOverviewStatus = (state) => state.stockOverview.list.status || "idle";
export const selectStockOverviewError = (state) => state.stockOverview.list.error || null;

export const selectBreakdown = (productId) => (state) =>
  state.stockOverview.breakdownByProductId[String(productId)] || [];
export const selectBreakdownStatus = (productId) => (state) =>
  state.stockOverview.breakdownStatusByProductId[String(productId)] || "idle";

export const selectStockActionStatus = (state) => state.stockOverview.actionStatus || "idle";
export const selectStockActionError = (state) => state.stockOverview.actionError || null;
